/**
 * TrpcMembershipResignationAdapter — the real `MembershipResignationPort` over
 * `member.resignation.submit` (056).
 *
 * A pass-through: the server binds the key to the member, calls the register,
 * audits and decides the outcome. Nothing is retried here and nothing is
 * remembered — no pending flag, no stored `submittedAt` (P7).
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type {
  MembershipResignationPort,
  ResignationIntent,
  SubmitResignationOutcome,
} from './MembershipResignationPort.js';

export class TrpcMembershipResignationAdapter implements MembershipResignationPort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async submitResignation(intent: ResignationIntent): Promise<SubmitResignationOutcome> {
    // Resolved OUTSIDE the try: an absent namespace is a composition error that
    // must fail loudly, not read as "try again shortly".
    const resignation = this.#resignation();
    try {
      const outcome = await resignation.submit.mutate({ idempotencyKey: intent.idempotencyKey });
      // Assigning the router-inferred output to the web union keeps the compile
      // guard: an arm the api adds that the web lacks fails typecheck here.
      const typed: SubmitResignationOutcome = outcome;
      return typed;
    } catch {
      // A rejected mutation (transport failure, expired session) is neither a
      // success nor a definitive refusal. "Could not confirm" is the only
      // truthful thing to say.
      return { status: 'refused', reason: 'unavailable' };
    }
  }

  #resignation() {
    const resignation = this.#client.member?.resignation;
    if (resignation === undefined) {
      throw new Error(
        'TrpcMembershipResignationAdapter: member.resignation is absent on the AppRouter — the ' +
          'resignation surface requires the IntegrationService client to be configured (056).',
      );
    }
    return resignation;
  }
}
